import { Component, Inject, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { Utils } from 'src/app/core/resources/utils';
import { SnackBarService } from 'src/app/core/services/snackBar.service';
import { FreightListService } from '../cargo-list/cargo-list.service';
import { Cargo } from 'src/app/core/interfaces/cargo';
import { Destination } from 'src/app/core/interfaces/destination';
import { CargoStateEnum } from 'src/app/core/enums/cargoState.enum';
import { DateManager } from 'src/app/core/managers/date.manager';
import { CargoManager } from 'src/app/core/managers/cargo.manager';

@Component({
    selector: 'app-replicate-service',
    templateUrl: './replicate-service.component.html',
    styleUrls: ['./replicate-service.component.scss']
})
export class ReplicateServiceComponent implements OnInit {

    serviceTypeAllowed: 'transport' | 'escort' | 'satelliteLock' = 'transport';
    consecutivesControl: FormControl = new FormControl(null);
    targetCargos: { cargo: Cargo, errorMessage?: string, checked: boolean }[] = [];
    private readonly MAX_SERVICES = 30;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            cargo: Cargo,
        },
        public dialogRef: MatDialogRef<ReplicateServiceComponent>,
        public utils: Utils,
        private spinner: NgxSpinnerService,
        private snackBarService: SnackBarService,
        private cargoListService: FreightListService,
        private cargoManager: CargoManager
    ) { }

    async ngOnInit() {
        if (!this.data || !this.data.cargo || !this.data.cargo.id) {
            this.snackBarService.openSnackBar("No se pudo identificar el servicio origen", undefined, 'alert');
            this.dialogRef.close();
            return;
        }
        if (this.data.cargo.creationDate) {
            let suggestedCargos = await this.findCargos('sameCreationDate');
            if (!suggestedCargos || !suggestedCargos.length) return;
            suggestedCargos = suggestedCargos.filter(cargo => cargo.consecutive !== this.data.cargo.consecutive);
            if (suggestedCargos.length) this.targetCargos.push(...suggestedCargos.map(cargo => this.processCargo(cargo)));
        }
        if (this.utils.isEscortedService(this.data.cargo)) this.serviceTypeAllowed = 'escort';
        else if (this.utils.isSatelliteLockService(this.data.cargo)) this.serviceTypeAllowed = 'satelliteLock';
        else this.serviceTypeAllowed = 'transport';
    }

    async searchConsecutives() {
        if (!this.consecutivesControl.value)
            return this.snackBarService.openSnackBar("Ingrese los consecutivos de los servicios a replicar", undefined, 'alert');
        const consecutives: string[] = `${this.consecutivesControl.value}`.split(',').map(c => c.trim()).filter(c => !!c);
        if (!consecutives.length)
            return this.snackBarService.openSnackBar("Ingrese al menos un consecutivo", undefined, 'alert');
        const sourceConsecutive = this.data && this.data.cargo && this.data.cargo.consecutive
            ? this.data.cargo.consecutive.toString()
            : '';
        const consecutivesToFind: string[] = consecutives.filter(
            consecutive => !this.targetCargos.some(
                selected => selected && selected.cargo && selected.cargo.consecutive.toString() === consecutive
            ) && consecutive !== sourceConsecutive
        );
        if (!consecutivesToFind.length)
            return this.snackBarService.openSnackBar("Todos los servicios ya están en la lista", undefined, 'alert');
        if (consecutivesToFind.length + this.targetCargos.length > this.MAX_SERVICES)
            return this.snackBarService.openSnackBar(`No se pueden replicar más de ${this.MAX_SERVICES} servicios`, undefined, 'alert');
        const listCargo = await this.findCargos('consecutives', consecutivesToFind);
        if (!listCargo || !listCargo.length)
            return this.snackBarService.openSnackBar("No se encontraron solicitudes con los consecutivos digitados", undefined, 'alert');
        this.targetCargos.push(...listCargo.map(cargo => this.processCargo(cargo)));
        const nonExistentList: string[] = consecutivesToFind.filter(
            consecutive => !listCargo.some(cargo => cargo && cargo.consecutive.toString() === consecutive)
        );
        if (nonExistentList.length > 0)
            this.snackBarService.openSnackBar(`No se encontraron solicitudes con los consecutivos: ${nonExistentList.join(', ')}`, undefined, 'alert');
        else
            this.snackBarService.openSnackBar("Solicitudes encontradas y agregadas correctamente", undefined, 'success');
        this.consecutivesControl.setValue('');
    }

    private async findCargos(mode: 'consecutives' | 'sameCreationDate', values?: string[]): Promise<Cargo[]> {
        try {
            this.spinner.show();
            let filters = '';
            const creationDate = DateManager.formatDate(this.data.cargo.creationDate, null, 'YYYY-MM-DD ZZ');
            filters += `&states=${encodeURIComponent(JSON.stringify([CargoStateEnum.REQUEST]))}&pageKey=1&pageSize=30`;
            if (mode === 'consecutives') filters += `&consecutives=${encodeURIComponent(JSON.stringify(values))}`;
            else if (mode === 'sameCreationDate') {
                filters += `&creationDateInitial=${encodeURIComponent(creationDate)}`;
                filters += `&creationDateFinal=${encodeURIComponent(creationDate)}`;
            }
            const response = await this.cargoListService.getListCargo(filters).toPromise();
            const cargos = response.results;
            this.spinner.hide();
            if (!cargos || !cargos.length) return [];
            if (mode === 'sameCreationDate')
                return cargos.filter(cargo => cargo.creationDate === this.data.cargo.creationDate);
            return cargos;
        } catch (error) {
            this.spinner.hide();
            return [];
        }
    }

    processCargo(cargo: Cargo): { cargo: Cargo, errorMessage?: string, checked: boolean } {
        if (!cargo) return { cargo, errorMessage: 'Servicio inválido', checked: false };
        if (cargo.state !== CargoStateEnum.REQUEST)
            return { cargo, errorMessage: `El servicio ${cargo.consecutive} no está en estado de solicitud`, checked: false };
        if (this.serviceTypeAllowed === 'transport' && !this.utils.isTransportService(cargo))
            return { cargo, errorMessage: `El servicio ${cargo.consecutive} no es de transporte`, checked: false };
        if (this.serviceTypeAllowed === 'escort' && !this.utils.isEscortedService(cargo))
            return { cargo, errorMessage: `El servicio ${cargo.consecutive} no es de escolta`, checked: false };
        if (this.serviceTypeAllowed === 'satelliteLock' && !this.utils.isSatelliteLockService(cargo))
            return { cargo, errorMessage: `El servicio ${cargo.consecutive} no es de candado satelital`, checked: false };
        return { cargo, checked: true };
    }

    getCargoLastDestination(cargo: Cargo): Destination {
        const destinations: Destination[] = this.utils.getNestedValue(cargo, 'cargoFeature.uploadDownload.destination');
        if (destinations && destinations.length) return destinations[destinations.length - 1];
        return null;
    }

    get validTargetCargos(): { cargo: Cargo, errorMessage?: string, checked: boolean }[] {
        return this.targetCargos.filter(row => row && !row.errorMessage);
    }

    get allValidSelected(): boolean {
        const validRows = this.validTargetCargos;
        return !!validRows.length && validRows.every(row => row.checked);
    }

    get someValidSelected(): boolean {
        const validRows = this.validTargetCargos;
        const selectedCount = validRows.filter(row => row.checked).length;
        return selectedCount > 0 && selectedCount < validRows.length;
    }

    setCargoChecked(index: number, checked: boolean) {
        const row = this.targetCargos[index];
        if (!row || row.errorMessage) return;
        row.checked = checked;
    }

    toggleSelectAll(checked: boolean) {
        this.validTargetCargos.forEach(row => row.checked = checked);
    }

    openCargoInNewTab(cargo: Cargo, event?: Event) {
        if (event) event.stopPropagation();
        if (!cargo) return;
        this.utils.openCargoNewTab(cargo);
    }

    async onSubmit() {
        if (!this.targetCargos.length || !this.targetCargos.some(selected => selected && selected.checked))
            return this.snackBarService.openSnackBar("Debes seleccionar al menos un servicio destino para replicar", undefined, 'alert');
        const cargos = this.targetCargos
            .filter(selected => selected && selected.cargo && selected.cargo.consecutive && selected.checked)
            .map(selected => selected.cargo);
        const consecutives: string[] = cargos.map(cargo => cargo.consecutive.toString());
        this.spinner.show();
        this.cargoListService.replicateService(this.data.cargo.id, consecutives).subscribe(
            (response: { successfulConsecutives: string[], failedConsecutives: { consecutive: string, error: string }[] }) => {
                this.spinner.hide();
                this.cargoManager.modalCargoCreation(cargos, `La información se replicó exitosamente en los siguientes servicios`, response.failedConsecutives, response.successfulConsecutives);
                this.dialogRef.close({ state: true });
            },
            (error) => {
                this.spinner.hide();
                const message = this.utils.getNestedValue(error, 'error.message')
                    || "Ocurrió un error al replicar el servicio";
                this.snackBarService.openSnackBar(message, undefined, 'error');
            }
        );
    }
}
